import React, { useEffect, useCallback } from 'react';

// Small reusable in-app confirmation modal. Use for destructive actions
// where window.confirm() feels too easy to bypass or out-of-product.
//
// Props:
//   open       — boolean, controls visibility
//   title      — string, dialog headline
//   message    — string or node, body text
//   confirmText — button label, defaults to "Delete"
//   cancelText  — button label, defaults to "Cancel"
//   destructive — if true, confirm button is red (default true)
//   onConfirm  — handler for the primary button
//   onCancel   — handler for cancel / backdrop click / Escape
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  destructive = true,
  onConfirm,
  onCancel,
}) {
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onCancel?.();
  }, [onCancel]);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onCancel?.();
  };

  const confirmBg = destructive ? '#ef4444' : '#01696F';

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '1.75rem',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 id="confirm-dialog-title" style={{
          fontSize: '1.05rem', fontWeight: '700', color: '#1e293b',
          margin: '0 0 0.5rem',
        }}>
          {title}
        </h2>
        <p style={{
          fontSize: '0.9rem', color: '#475569', margin: '0 0 1.5rem',
          lineHeight: 1.5,
        }}>
          {message}
        </p>
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '0.6rem',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '0.55rem 1.1rem', borderRadius: '8px',
              fontSize: '0.875rem', fontWeight: '600',
              backgroundColor: '#ffffff', color: '#475569',
              border: '1px solid #cbd5e1',
              cursor: 'pointer',
              minHeight: '40px',
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            style={{
              padding: '0.55rem 1.1rem', borderRadius: '8px',
              fontSize: '0.875rem', fontWeight: '700',
              backgroundColor: confirmBg, color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              minHeight: '40px',
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
